import React, { useEffect, useState } from 'react';
import '../styles/Header.scss';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationPin } from '@fortawesome/free-solid-svg-icons';
import { Phone, Globe, ChevronDown, Menu, X, ArrowRight } from 'lucide-react';

const socialLinks = [
  {
    href: 'https://www.google.com/search?q=prabhat+tara+vidya+niketan',
    label: 'Google Reviews',
    icon: '/icons/social-media/review-us.png',
    alt: 'Review us'
  },
  {
    href: 'https://www.facebook.com/profile.php?id=61562571579177',
    label: 'Facebook Page',
    icon: '/icons/social-media/facebook.png',
    alt: 'Facebook'
  },
  {
    href: 'https://www.instagram.com/prabhattaravidyaniketan/',
    label: 'Instagram Profile',
    icon: '/icons/social-media/instagram.png',
    alt: 'Instagram'
  },
  {
    href: 'https://www.youtube.com/@ptvn_goraul',
    label: 'YouTube Channel',
    icon: '/icons/social-media/youtube.png',
    alt: 'YouTube'
  }
];

const Header = () => {
  const { t } = useTranslation();
  const [showLanguagePrompt, setShowLanguagePrompt] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(window.innerWidth >= 768);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobileView(mobile);
      if (mobile) {
        setIsMenuOpen(false);
      } else {
        setIsMenuOpen(true);
      }
    };

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    const languageSet = localStorage.getItem('languageSet');
    if (!languageSet) {
      setShowLanguagePrompt(true);
    }

    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('languageSet', 'true');
    setShowLanguagePrompt(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const LanguagePrompt = () => (
    <div className="language-prompt">
      <section>
        <p>PLEASE CHOOSE YOUR LANGUAGE</p>
        <button onClick={() => handleLanguageChange('hi')}>
          <img src="/icons/ind.png" alt="Hindi Flag" />
          हिंदी
        </button>
        <button onClick={() => handleLanguageChange('en')}>
          <img src="/icons/eng.png" alt="English Flag" />
          English
        </button>
      </section>
    </div>
  );

  return (
    <header>
      {showLanguagePrompt && <LanguagePrompt />}

      {/* Powered by ScanScore Top Bar */}
      <div className="top-scanscore-bar">
        <div className="scanscore-bar-inner">
          <div className="scanscore-tag">
            <span className="pulse-dot" aria-hidden="true"></span>
            <span className="scanscore-tag-label">{t('powered_by')}</span>
            <a 
              href="https://scanscore.app" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="scanscore-brand-link"
              title="Visit ScanScore (scanscore.app) - Phone OMR Scanner for Teachers & Schools"
            >
              ScanScore (scanscore.app)
            </a>
          </div>
          <span className="scanscore-bar-divider" aria-hidden="true">•</span>
          <p className="scanscore-bar-tagline">
            {t('scanscore_tagline')}
          </p>
          <div className="scanscore-bar-action">
            <a 
              href="/scanscore-assessment" 
              className="scanscore-learn-more"
              title="Read how ScanScore eases teacher workload and transforms school testing"
            >
              <span>{t('how_scanscore_eases_work')}</span>
              <ArrowRight size={13} />
            </a>
          </div>
        </div>
      </div>
      
      {/* Top section with contact and social links */}
      <div className={`top ${isScrolled ? 'scrolled' : ''}`}>
        <section className="call_us">
          <a href="tel:7654164726" className="header-call-pill" aria-label="Call Prabhat Tara Vidya Niketan">
            <Phone size={15} />
            <span>+91 76541 64726</span>
          </a>
          <button 
            onClick={() => setShowLanguagePrompt(true)}
            className="header-lang-btn"
            aria-label="Change Language"
          >
            <Globe size={15} />
            <span>{i18n.language === 'hi' ? 'हिंदी' : 'English'}</span>
          </button>
        </section>
        
        <section className="social_media_links">
          {socialLinks.map(({ href, label, icon, alt }) => (
            <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} title={label}>
              <img src={icon} alt={alt} loading="lazy" />
            </a>
          ))}
        </section>
      </div>

      {/* Center section with logo and school information */}
      <div className="center">
        <div className="center-content">
          <div className="center-row">
            <section className="logo_name">
              <a href="/">
                <img src="/images/logo.jpg" alt="Prabhat Tara Vidya Niketan Logo" />
              </a>
            </section>
            <section className="years_of_service">
              <img src="/images/yrs-of-service.png" alt="30 Years of Service Badge" />
            </section>
          </div>
          
          <div className="center-text">
            <h1>{t('school_name')}</h1>
            <p>
              <FontAwesomeIcon icon={faLocationPin} /> 
              {t('school_address')}
            </p>
            <p>{t('school_tagline_bar')}</p>
          </div>
        </div>
      </div>

      {/* Navigation section */}
      <div className="bottom">
        {isMobileView && (
          <button onClick={toggleMenu} className="mobile-toggle-btn" aria-label="Toggle Navigation Menu">
            {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
            <span>{t('menu')}</span>
          </button>
        )}
        
        {isMenuOpen && (
          <nav>
            {/* Home */}
            <section>
              <a href="/">{t('home')}</a>
            </section>
            
            {/* 1. About Us */}
            <section className="has-dropdown">
              <a href="/about-us">
                <span>{t('about_us')}</span>
                <ChevronDown size={14} className="nav-chevron" />
              </a>
              <div>
                <a href="/about-us#about-school">{t('nav_about_school')}</a>
                <a href="/principal-message">{t('nav_principal_desk')}</a>
                <a href="/about-us#affiliation">{t('nav_affiliation')}</a>
                <a href="/about-us#facilities">{t('nav_facilities')}</a>
                <a href="/mandatory-disclosure">{t('nav_mandatory_disclosure')}</a>
                <a href="/about-us#faqs">{t('nav_faqs')}</a>
                <a href="/about-us#vision-mission">{t('nav_vision_mission')}</a>
                <a href="/about-us#history-achievements">{t('nav_history')}</a>
                <a href="/about-us#management-committee">{t('nav_management_committee')}</a>
              </div>
            </section>
            
            {/* 2. Academics */}
            <section className="has-dropdown">
              <a href="/academics">
                <span>{t('academics')}</span>
                <ChevronDown size={14} className="nav-chevron" />
              </a>
              <div>
                <a href="/academics#holiday-list">{t('nav_holiday_list')}</a>
                <a href="/academics#academic-calendar">{t('nav_academic_calendar')}</a>
                <a href="/academics#school-timings">{t('nav_school_timings')}</a>
                <a href="/uniform-schedule">{t('nav_school_uniform')}</a>
                <a href="/academics#class-syllabus">{t('nav_class_syllabus')}</a>
                <a href="/academics#teaching-methodology">{t('nav_teaching_methodology')}</a>
                <a href="/academics#examination-schedule">{t('nav_examination_schedule')}</a>
                <a href="/academics#results">{t('nav_results')}</a>
                <a href="/academics#co-curricular">{t('nav_cocurricular')}</a>
                <a href="/scanscore-assessment">{t('nav_scanscore')}</a>
              </div>
            </section>

            {/* 3. The School */}
            <section className="has-dropdown">
              <a href="/the-school">
                <span>{t('the_school')}</span>
                <ChevronDown size={14} className="nav-chevron" />
              </a>
              <div>
                <a href="/the-school#rules-regulations">{t('nav_rules')}</a>
                <a href="/the-school#message-students">{t('nav_msg_students')}</a>
                <a href="/the-school#message-parents">{t('nav_msg_parents')}</a>
                <a href="/the-school#infrastructure">{t('nav_infrastructure')}</a>
                <a href="/teaching-staff">{t('nav_teaching_faculty')}</a>
                <a href="/the-school#school-management">{t('nav_school_management')}</a>
              </div>
            </section>

            {/* 4. Admission */}
            <section className="has-dropdown">
              <a href="/admission">
                <span>{t('admission')}</span>
                <ChevronDown size={14} className="nav-chevron" />
              </a>
              <div>
                <a href="/admission#admission-process">{t('nav_admission_process')}</a>
                <a href="/admission#required-documents">{t('nav_required_documents')}</a>
                <a href="/admission#age-criteria">{t('nav_age_criteria')}</a>
                <a href="/admission#admission-guidelines">{t('nav_admission_guidelines')}</a>
                <a href="/admission#enquiry-info">{t('nav_online_enquiry')}</a>
                <a href="/admission#enquiry-form">{t('nav_enquiry_form')}</a>
                <a href="/admission#admission-faqs">{t('nav_admission_faqs')}</a>
              </div>
            </section>

            {/* 5. Gallery */}
            <section className="has-dropdown">
              <a href="/gallery">
                <span>{t('gallery')}</span>
                <ChevronDown size={14} className="nav-chevron" />
              </a>
              <div className="dropdown-right">
                <a href="/gallery#photo-gallery">{t('nav_photo_gallery')}</a>
                <a href="/gallery#video-gallery">{t('nav_video_gallery')}</a>
                <a href="/gallery#annual-function">{t('nav_annual_function')}</a>
                <a href="/gallery#sports-cultural">{t('nav_sports_cultural')}</a>
                <a href="/gallery#tours-events">{t('nav_tours_events')}</a>
              </div>
            </section>

            {/* 6. Fee Structure */}
            <section className="has-dropdown">
              <a href="/fee-structure">
                <span>{t('fee_structure')}</span>
                <ChevronDown size={14} className="nav-chevron" />
              </a>
              <div className="dropdown-right">
                <a href="/fee-structure#class-wise-fees">{t('nav_class_fees')}</a>
                <a href="/fee-structure#admission-fee">{t('nav_admission_fee')}</a>
                <a href="/fee-structure#monthly-tuition">{t('nav_monthly_tuition')}</a>
                <a href="/fee-structure#other-charges">{t('nav_other_charges')}</a>
                <a href="/fee-structure#payment-guidelines">{t('nav_payment_guidelines')}</a>
              </div>
            </section>

            {/* 7. Contact Us */}
            <section className="has-dropdown">
              <a href="/contact-us">
                <span>{t('contact_us')}</span>
                <ChevronDown size={14} className="nav-chevron" />
              </a>
              <div className="dropdown-right">
                <a href="/contact-us#school-address">{t('nav_address')}</a>
                <a href="/contact-us#contact-numbers">{t('nav_phone_numbers')}</a>
                <a href="/contact-us#email-address">{t('nav_email_address')}</a>
                <a href="/contact-us#maps-location">{t('nav_maps_location')}</a>
                <a href="/contact-us#contact-form">{t('nav_enquiry_desk')}</a>
                <a href="/contact-us#working-hours">{t('nav_working_hours')}</a>
              </div>
            </section>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
